/**
 * LogRocket-Gate (Issue #93).
 *
 * Seit dem zentralen Deploy gibt es genau ein Bundle für alle Mandanten —
 * jede Build-Zeit-Variable ist damit ein Flottenschalter. Mandantenspezifische
 * Aktivierung läuft deshalb über eine Hostname-Allowlist
 * (`REACT_APP_LOGROCKET_TENANTS`, kommasepariert), die gegen den Tenant aus
 * `window.location.hostname` geprüft wird. Leer = niemand (fail-closed).
 *
 * Leaf-Modul: darf nichts aus `App.tsx`/Containern importieren (Require-Zyklus).
 * Das `logrocket`-Paket wird erst per `import()` geladen, wenn das Gate offen
 * ist — der npm-Shim hängt sein CDN-Script sonst schon beim Modul-Import in
 * `document.head`, für alle Mandanten.
 */

import type LogRocketType from "logrocket"

const LOGROCKET_APP_ID = process.env.REACT_APP_LOGROCKET_APP_ID
const LOGROCKET_TENANTS = process.env.REACT_APP_LOGROCKET_TENANTS

/**
 * Tenant-Kennung aus dem Hostnamen ableiten.
 * app.babysplash.kursorganizer.com → "babysplash"
 * app.stage.dev-schule.kursorganizer.com → roher Hostname (kein 3-Label-Match)
 * localhost → "localhost"
 */
export function getTenantFromHostname(hostname: string = readHostname()): string {
    const match = hostname.match(/^app\.([^.]+)\.kursorganizer\.com$/)
    return match ? match[1] : hostname
}

function readHostname(): string {
    try {
        return (window.location.hostname ?? "").toLowerCase()
    } catch (e) {
        return "unknown"
    }
}

/** Kommaseparierte Allowlist parsen: trim, lowercase, leere Einträge raus. Kein Wildcard. */
export function parseTenantAllowlist(raw: string | undefined): string[] {
    if (!raw) return []
    return raw
        .split(",")
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.length > 0 && entry !== "*")
}

export function isTenantAllowed(hostname: string, rawAllowlist: string | undefined): boolean {
    const allowlist = parseTenantAllowlist(rawAllowlist)
    if (allowlist.length === 0) return false
    const lowerHost = hostname.toLowerCase()
    if (lowerHost === "localhost") return false
    return allowlist.includes(getTenantFromHostname(lowerHost))
}

/** Ist LogRocket für den aktuellen Hostnamen (und mit App-ID) freigeschaltet? */
export function isLogRocketEnabled(): boolean {
    return !!LOGROCKET_APP_ID && isTenantAllowed(readHostname(), LOGROCKET_TENANTS)
}

type LogRocketModule = typeof LogRocketType

let modulePromise: Promise<LogRocketModule> | undefined

function loadLogRocket(): Promise<LogRocketModule> {
    if (!modulePromise) {
        // `export =`-Modul: je nach Interop liegt die Instanz auf `default` oder direkt am Modul.
        modulePromise = import("logrocket").then((mod) => {
            const withDefault = mod as unknown as { default?: LogRocketModule }
            return withDefault.default ?? (mod as unknown as LogRocketModule)
        })
    }
    return modulePromise
}

/**
 * Aufzeichnung starten. No-op außerhalb der Allowlist.
 * Sanitizer standardmäßig an: nach dem Login sind Teilnehmer-, Zahlungs- und
 * SEPA-Felder im aufgezeichneten Bereich.
 */
export async function initLogRocket(): Promise<void> {
    if (!isLogRocketEnabled() || !LOGROCKET_APP_ID) return
    const LogRocket = await loadLogRocket()
    LogRocket.init(LOGROCKET_APP_ID, {
        dom: { inputSanitizer: true, textSanitizer: true },
        network: { requestSanitizer: () => null, responseSanitizer: () => null },
    })
}

export type LogRocketIdentity = {
    uid: string
    traits: Record<string, string | number | boolean>
}

/** Nutzer der Session zuordnen. No-op außerhalb der Allowlist — nie ohne Gate. */
export async function identifyLogRocket({ uid, traits }: LogRocketIdentity): Promise<void> {
    if (!isLogRocketEnabled()) return
    const LogRocket = await loadLogRocket()
    LogRocket.identify(uid, traits)
}

/**
 * Beim Logout die identifizierte Session beenden. Logout lädt die Seite nicht
 * neu, das SDK würde sonst unter der alten Bucher-Identität weiter aufzeichnen.
 * Nur wirksam, wenn das Paket bereits geladen wurde (also init lief).
 */
export async function resetLogRocketSession(): Promise<void> {
    if (!modulePromise) return
    const LogRocket = await modulePromise
    LogRocket.startNewSession()
}
